import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { trackDeckCreated } from '../analytics';
import DesignPreview from '../components/DesignPreview';
import SlideThumbnail from '../components/SlideThumbnail';
import TemplateCardStats from '../components/TemplateCardStats';
import { createDeck } from '../api/decks';
import { createStarterSlides } from '../data/deckDefaults';
import { saveDesignId } from '../data/designStore';
import { saveActiveDeck } from '../data/deckStore';
import { DEFAULT_DESIGN_ID, getDesignOrDefault } from '../data/designTemplates';
import {
  getPitchTemplateOrDefault,
  getTemplatePalette,
  visiblePitchTemplates,
} from '../data/pitchTemplates';
import { saveSlides } from '../data/slidesStore';
import { usePitchTemplateMeta } from '../i18n/usePitchTemplateMeta';
import { useTemplateStats } from '../hooks/useTemplateStats';
import { appTheme, pitchTheme } from '../styles/theme';

const BLANK_TEMPLATE_ID = '__blank__';

const Page = styled.main`
  flex: 1;
  padding: 48px 24px 64px;
  background: ${appTheme.pageBg};
`;

const Container = styled.div`
  max-width: ${appTheme.maxWidth};
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 32px;
`;

const Header = styled.header`
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-width: 640px;
`;

const Title = styled.h1`
  margin: 0;
  font-family: ${pitchTheme.fontPageTitle};
  font-size: clamp(1.75rem, 3vw, 2.25rem);
  font-weight: 500;
  line-height: 1.15;
  letter-spacing: -0.02em;
  color: ${appTheme.greenDark};
`;

const Intro = styled.p`
  margin: 0;
  font-size: 1rem;
  line-height: 1.6;
  color: ${appTheme.textMuted};
`;

const TemplateGrid = styled.div`
  display: grid;
  gap: 20px;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
`;

const TemplateCard = styled.button<{ $busy: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 20px;
  border-radius: ${appTheme.radiusLg};
  border: 1px solid ${appTheme.border};
  background: ${appTheme.surface};
  font-family: inherit;
  text-align: left;
  cursor: ${({ $busy }) => ($busy ? 'wait' : 'pointer')};
  opacity: ${({ $busy }) => ($busy ? 0.85 : 1)};
  transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;

  &:hover:not(:disabled) {
    border-color: ${appTheme.green};
    box-shadow: ${appTheme.shadowMd};
    transform: translateY(-2px);
  }

  &:focus-visible {
    outline: 2px solid ${appTheme.green};
    outline-offset: 2px;
  }

  &:disabled {
    cursor: wait;
  }
`;

const CardTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const IndustryBadge = styled.span`
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${appTheme.green};
  background: ${appTheme.mint};
  border-radius: ${appTheme.radiusPill};
  padding: 4px 10px;
`;

const SlideCount = styled.span`
  font-size: 0.75rem;
  font-weight: 600;
  color: ${appTheme.textMuted};
`;

const TemplateName = styled.span`
  font-size: 1.0625rem;
  font-weight: 500;
  color: ${appTheme.text};
`;

const TemplateDescription = styled.span`
  font-size: 0.8125rem;
  line-height: 1.5;
  color: ${appTheme.textMuted};
`;

const ErrorMessage = styled.p`
  margin: 0;
  padding: 12px 16px;
  border-radius: ${appTheme.radiusMd};
  background: ${appTheme.dangerBg};
  border: 1px solid ${appTheme.dangerBorder};
  font-size: 0.875rem;
  font-weight: 600;
  color: ${appTheme.dangerText};
`;

const NewDeckPage: React.FC = () => {
  const { t } = useTranslation();
  const { getTemplateName, getTemplateDescription, getTemplateIndustry } = usePitchTemplateMeta();
  const { getStat, isLiked, toggleLike, bumpUsage, likingId } = useTemplateStats();
  const navigate = useNavigate();
  const [creatingId, setCreatingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const openTemplateInEditor = async (templateId: string): Promise<void> => {
    if (creatingId) {
      return;
    }

    setCreatingId(templateId);
    setError(null);

    try {
      if (templateId === BLANK_TEMPLATE_ID) {
        const deck = await createDeck({
          name: t('newDeck.untitledName'),
          designId: DEFAULT_DESIGN_ID,
          slides: createStarterSlides(),
        });
        saveSlides(deck.slides);
        saveDesignId(deck.designId);
        saveActiveDeck(deck.id, deck.name, deck.designId);
        trackDeckCreated({
          template_id: BLANK_TEMPLATE_ID,
          slide_count: deck.slides.length,
          source: 'new_deck_page',
        });
      } else {
        const template = getPitchTemplateOrDefault(templateId);
        const deck = await createDeck({
          name: template.name,
          designId: template.designId,
          slides: createStarterSlides(templateId),
          pitchTemplateId: templateId,
        });
        bumpUsage(templateId);
        saveSlides(deck.slides);
        saveDesignId(deck.designId);
        saveActiveDeck(deck.id, deck.name, deck.designId);
        trackDeckCreated({
          template_id: templateId,
          slide_count: deck.slides.length,
          source: 'new_deck_page',
        });
      }
      navigate('/edit');
    } catch (err) {
      setError(err instanceof Error ? err.message : t('newDeck.createError'));
      setCreatingId(null);
    }
  };

  const blankPalette = getDesignOrDefault(DEFAULT_DESIGN_ID).palette;

  return (
    <Page>
      <Container>
        <Header>
          <Title>{t('newDeck.title')}</Title>
          <Intro>{t('newDeck.intro')}</Intro>
        </Header>

        {error && <ErrorMessage role="alert">{error}</ErrorMessage>}

        <TemplateGrid>
          <TemplateCard
            type="button"
            $busy={creatingId === BLANK_TEMPLATE_ID}
            disabled={creatingId !== null}
            onClick={() => openTemplateInEditor(BLANK_TEMPLATE_ID)}
          >
            <DesignPreview palette={blankPalette} />
            <CardTop>
              <IndustryBadge>{t('newDeck.blank.industry')}</IndustryBadge>
              <SlideCount>{t('newDeck.blank.slideCount', { count: 1 })}</SlideCount>
            </CardTop>
            <TemplateName>
              {creatingId === BLANK_TEMPLATE_ID
                ? t('newDeck.blank.creating')
                : t('newDeck.blank.name')}
            </TemplateName>
            <TemplateDescription>{t('newDeck.blank.description')}</TemplateDescription>
          </TemplateCard>

          {visiblePitchTemplates.map((template) => (
            <TemplateCard
              key={template.id}
              type="button"
              $busy={creatingId === template.id}
              disabled={creatingId !== null}
              onClick={() => openTemplateInEditor(template.id)}
            >
              {template.sampleSlides[0] && (
                <SlideThumbnail
                  slide={template.sampleSlides[0]}
                  palette={getTemplatePalette(template)}
                />
              )}
              <CardTop>
                <IndustryBadge>{getTemplateIndustry(template)}</IndustryBadge>
                <SlideCount>
                  {t('templates.carousel.slideCount', { count: template.sampleSlides.length })}
                </SlideCount>
              </CardTop>
              <TemplateCardStats
                usageCount={getStat(template.id).usageCount}
                likeCount={getStat(template.id).likeCount}
                liked={isLiked(template.id)}
                liking={likingId === template.id}
                onToggleLike={(event) => {
                  void toggleLike(template.id, event);
                }}
              />
              <TemplateName>
                {creatingId === template.id
                  ? t('newDeck.blank.creating')
                  : getTemplateName(template)}
              </TemplateName>
              <TemplateDescription>{getTemplateDescription(template)}</TemplateDescription>
            </TemplateCard>
          ))}
        </TemplateGrid>
      </Container>
    </Page>
  );
};

export default NewDeckPage;
